import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  forwardRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-ransomware-leak-site',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule
],
  templateUrl: './form-ransomware-leak-site.component.html',
  styleUrl: './form-ransomware-leak-site.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormRansomwareLeakSiteComponent),
      multi: true,
    },
  ],
})
export class FormRansomwareLeakSiteComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input() languages = ['en'];

  formSite: FormGroup;
  private onChange = (value: any) => {};
  private destroy$ = new Subject<void>();

  private onTouched = () => {};
  isLoading = true;

  constructor(private fb: FormBuilder) {
    this.formSite = this.fb.group({
      items: this.fb.array([]),
    });

    this.createInitialEnglishItem();
  }

  ngOnInit() {
    this.formSite.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onChange(value.items);
        this.onTouched();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['languages']) {
      if (!changes['languages'].firstChange) {
        this.updateFormControls();
      }
    }
  }

  private createInitialEnglishItem() {
    if (this.itemsArray.length === 0) {
      const englishItem = this.createEnglishItem();
      this.itemsArray.push(englishItem);
    }
  }

  private updateFormControls() {
    this.itemsArray.controls.forEach((control, index) => {
      const titleControl = control.get('title') as FormGroup;
      const textControl = control.get('content') as FormGroup;

      if (titleControl && textControl) {
        this.languages.forEach((lang) => {
          if (!textControl.contains(lang)) {
            textControl.addControl(lang, this.fb.control(''));
          }
          if (!titleControl.contains(lang)) {
            titleControl.addControl(lang, this.fb.control(''));
          }
        });

        const existingTitleLangs = Object.keys(titleControl.controls);
        const existingTextLangs = Object.keys(textControl.controls);

        existingTitleLangs.forEach((lang) => {
          if (!this.languages.includes(lang)) {
            titleControl.removeControl(lang);
          }
        });

        existingTextLangs.forEach((lang) => {
          if (!this.languages.includes(lang)) {
            textControl.removeControl(lang);
          }
        });
      }
    });
  }

  private createEnglishItem(): FormGroup {
    const titleGroup = this.fb.group({
      en: [''],
    });
    const textGroup = this.fb.group({
      en: [''],
    });

    return this.fb.group({
      status: [''],
      time: [''],
      title: titleGroup,
      content: textGroup,
      price: [''],
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemsArray(): FormArray {
    return this.formSite.get('items') as FormArray;
  }

  addItem() {
    const newItem = this.createItem();
    this.itemsArray.push(newItem);
  }

  removeItem(index: number) {
    this.itemsArray.removeAt(index);
  }

  private createItem(): FormGroup {
    const titleGroup = this.fb.group({});
    const textGroup = this.fb.group({});

    if (this.languages && this.languages.length > 0) {
      this.languages.forEach((lang) => {
        titleGroup.addControl(lang, this.fb.control(''));
        textGroup.addControl(lang, this.fb.control(''));
      });
    } else {
      titleGroup.addControl('en', this.fb.control(''));
      textGroup.addControl('en', this.fb.control(''));
    }

    return this.fb.group({
      status: [''],
      price: [''],
      time: [''],
      title: titleGroup,
      content: textGroup,
    });
  }

  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      this.itemsArray.clear();
      value.forEach((item, index) => {
        const itemGroup = this.createItem();
        itemGroup.patchValue(item);
        this.itemsArray.push(itemGroup);
      });
    } else if (this.itemsArray.length === 0) {
      this.createInitialEnglishItem();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formSite.disable();
    } else {
      this.formSite.enable();
    }
  }

  addLanguage(lang: string) {
    if (!this.languages.includes(lang)) {
      this.languages.push(lang);
      this.updateFormControls();
    }
  }

  removeLanguage(lang: string) {
    const index = this.languages.indexOf(lang);
    if (index > -1 && this.languages.length > 1) {
      this.languages.splice(index, 1);
      this.updateFormControls();
    }
  }
}
